import { useEffect, useState } from 'react';

import { DashboardPage } from '../features/counter/DashboardPage';
import { DebugFixtureImport } from '../features/debug/DebugFixtureImport';
import { OnboardingPage } from '../features/onboarding/OnboardingPage';
import { WrappedPage } from '../features/wrapped/WrappedPage';
import { installationStore } from '../services/persistence/identity-store';

export function App() {
  const [addingGroup, setAddingGroup] = useState(false);
  const [identityVersion, setIdentityVersion] = useState(0);
  const [path, setPath] = useState(() => window.location.pathname);
  const [dashboardTab, setDashboardTab] = useState<'today' | 'group'>(() => readNavigation().tab);
  const [photoId, setPhotoId] = useState(() => readNavigation().photoId);
  const [requestedGroupId, setRequestedGroupId] = useState(() => readNavigation().groupId);
  const installation = installationStore.read();

  useEffect(() => {
    if (requestedGroupId === null) return;
    const selected = installationStore.selectedMembership();
    if (selected?.groupId !== requestedGroupId && installationStore.selectGroup(requestedGroupId)) {
      window.setTimeout(() => setIdentityVersion((current) => current + 1), 0);
    }
  }, [requestedGroupId]);

  useEffect(() => {
    const onPopState = () => {
      setPath(window.location.pathname);
      const navigation = readNavigation();
      setDashboardTab(navigation.tab);
      setPhotoId(navigation.photoId);
      setRequestedGroupId(navigation.groupId);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  function navigate(nextPath: string, replace = false) {
    if (replace) {
      window.history.replaceState(null, '', nextPath);
    } else {
      window.history.pushState(null, '', nextPath);
    }
    setPath(nextPath);
    const navigation = readNavigation(new URL(nextPath, window.location.origin));
    setDashboardTab(navigation.tab);
    setPhotoId(navigation.photoId);
    setRequestedGroupId(navigation.groupId);
  }

  function refreshIdentity() {
    setIdentityVersion((current) => current + 1);
  }

  const debugFixtureImport = (
    <DebugFixtureImport
      onComplete={() => {
        navigate('/dashboard', true);
        refreshIdentity();
      }}
    />
  );

  if (installation === null) {
    return (
      <>
        {debugFixtureImport}
        <OnboardingPage
          onComplete={() => {
            navigate('/dashboard', true);
            refreshIdentity();
          }}
        />
      </>
    );
  }

  if (addingGroup || installation.memberships.length === 0) {
    return (
      <>
        {debugFixtureImport}
        <OnboardingPage
          onComplete={() => {
            setAddingGroup(false);
            navigate('/dashboard', true);
            refreshIdentity();
          }}
          {...(installation.memberships.length === 0
            ? {}
            : { onCancel: () => setAddingGroup(false) })}
        />
      </>
    );
  }

  if (path === '/wrapped') {
    return <WrappedPage onBack={() => navigate('/dashboard')} />;
  }

  return (
    <>
      {debugFixtureImport}
      <DashboardPage
        key={identityVersion}
        initialTab={dashboardTab}
        initialPhotoId={photoId}
        onClosePhoto={() => navigate('/dashboard', true)}
        onAddGroup={() => setAddingGroup(true)}
        onOpenWrapped={() => navigate('/wrapped')}
        onIdentityChange={(tab = 'today') => {
          setDashboardTab(tab);
          refreshIdentity();
        }}
        onExit={() => {
          navigate('/', true);
          refreshIdentity();
        }}
      />
    </>
  );
}

function readNavigation(url: URL = new URL(window.location.href)) {
  return {
    tab: url.searchParams.get('tab') === 'group' ? ('group' as const) : ('today' as const),
    photoId: url.searchParams.get('photoId'),
    groupId: url.searchParams.get('groupId'),
  };
}
